import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faTimes,
  faUserPlus,
  faChevronDown,
  faSpinner,
  faPlus,
  faMapMarkerAlt,
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../utils/axiosConfig';

// Search-as-you-type CRMProfile picker.
//
// Originally written for Section 2 (Customer) on the Fuel Enquiry form
// scoped to Fuels debtors. May 2026 it was generalised so the supplier
// slot in Section 6 can share the same UX — the PM didn't want a plain
// <select> in one section and a search box in the other.
//
// Props:
// - ``customer`` (object | null): the currently linked CRMProfile blob.
//   The legacy name is preserved for backwards-compat — for non-customer
//   uses (e.g. supplier picker) just pass the equivalent profile blob.
// - ``onChange(profile | null)``: invoked with the picked profile or
//   ``null`` when the user clears the selection.
// - ``searchParams`` (object, optional): extra query params merged into
//   the ``/api/crm/profiles/`` request. Defaults to
//   ``{ fuels_debtors_only: 'true' }`` (customer mode). Pass
//   ``{ fuels_suppliers_only: 'true' }`` for suppliers, etc.
// - ``allowUnlinked`` (bool, default true): when false, hides the
//   "+ Add unlinked customer" CTA — used by the supplier picker which
//   only ever links to a real CRMProfile.
// - ``placeholder`` (string, optional): override the search input
//   placeholder copy.
// - ``noResultsCopy`` (string, optional): override the empty-state copy.
// - ``onUnlinkedToggle(showUnlinked)``: only meaningful when
//   ``allowUnlinked`` is true.
// - ``showUnlinked`` (bool): mirror of the parent's unlinked-mode toggle.
// - ``disabled`` (bool): freeze interactions while saving.
//
// Keyboard support is intentionally minimal (no arrow keys) to keep the
// component small. The PM team uses the mouse; keyboard polish can be
// added in a later sprint if requested.

const DEBOUNCE_MS = 250;
const PAGE_SIZE = 20;
// Ask the server for ONE more row than we intend to draw.
//
// `/api/crm/quick-search-profiles/` answers with a bare array: no count, no
// next link, nothing that says whether anything was left behind. So the
// picker used to print "Showing the first 20 matches" whenever twenty rows
// came back -- which is right when there are six hundred COSCOs and a plain
// lie when there are exactly twenty. A warning that fires on ordinary
// searches is one people stop reading, and then the real truncation goes past
// them too.
//
// Asking for twenty-one and drawing twenty turns a guess into a fact: a
// twenty-first row came back, so there IS more; it did not, so there is not.
const PEEK_SIZE = PAGE_SIZE + 1;
const DEFAULT_SEARCH_PARAMS = { fuels_debtors_only: 'true' };

// Small grey line under a partner so the user can confirm they picked the
// right entity. Same look as DeliveryPartnerSearch / RecapPartnerSearch.
const ADDRESS_STYLE = {
  fontSize: '0.8em',
  fontWeight: 400,
  color: '#888',
  marginTop: '2px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

// The quick-search row carries a flat ``head_office_address``; the full CRM
// profile blob carries ``head_office_address_data`` instead.
const profileAddress = (p) => (
  p?.head_office_address
  || p?.head_office_address_data?.formatted_address
  || p?.head_office_address_data?.place_name
  || ''
);

const useDebouncedValue = (value, delayMs) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(handle);
  }, [value, delayMs]);
  return debounced;
};

const CustomerPicker = ({
  customer,
  onChange,
  onUnlinkedToggle,
  showUnlinked = false,
  disabled = false,
  searchParams = DEFAULT_SEARCH_PARAMS,
  allowUnlinked = true,
  placeholder: placeholderOverride = null,
  noResultsCopy = (
    'No matching Bunker debtors. Try a different search term, or add an '
    + 'unlinked customer below.'
  ),
  // Two-way write: when provided, shows a "+ Create CRM partner" CTA in
  // the dropdown. Receives the current search term so the caller can seed
  // the inline creator modal. Mirrors RecapPartnerSearch / DeliveryPartnerSearch.
  onCreatePartner,
  // Just-created Pending stubs to surface alongside live results so the
  // user can pick the profile they just created without waiting for approval.
  extraVisibleProfiles = [],
}) => {
  const [searchInput, setSearchInput] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState([]);
  // Fact, not guess: did the server hand back a row beyond the ones we drew?
  const [hasMoreMatches, setHasMoreMatches] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const wrapperRef = useRef(null);
  const requestIdRef = useRef(0);
  const debouncedSearch = useDebouncedValue(searchInput, DEBOUNCE_MS);

  useEffect(() => {
    const handleDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleDocClick);
    return () => document.removeEventListener('mousedown', handleDocClick);
  }, []);

  const runSearch = async (term) => {
    const reqId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      // Use the IsAuthenticated-only quick-search endpoint so trading users
      // without HasCRMAccess can still see existing partners. The endpoint
      // honours the same ``fuels_debtors_only`` / ``fuels_suppliers_only``
      // filters as the main viewset, so the picker behaves identically.
      const params = {
        ...searchParams,
        page_size: PEEK_SIZE,
      };
      if (term) params.search = term;
      const response = await axiosInstance.get(
        '/api/crm/quick-search-profiles/',
        { params }
      );
      if (reqId !== requestIdRef.current) return;
      const data = response.data;
      const rows = Array.isArray(data) ? data : (data?.results || []);
      // A `count` is better still, for the day this endpoint grows one.
      const stated = typeof data?.count === 'number' ? data.count : null;
      setHasMoreMatches(
        stated !== null ? stated > PAGE_SIZE : rows.length > PAGE_SIZE
      );
      setResults(rows.slice(0, PAGE_SIZE));
    } catch (err) {
      if (reqId !== requestIdRef.current) return;
      console.error('CustomerPicker search failed:', err);
      setError(err?.response?.data?.detail || err?.message || 'Search failed.');
      setResults([]);
      setHasMoreMatches(false);
    } finally {
      if (reqId === requestIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    runSearch(debouncedSearch.trim());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, isOpen]);

  const handlePick = async (row) => {
    setIsOpen(false);
    setSearchInput('');
    if (showUnlinked) {
      onUnlinkedToggle?.(false);
    }
    // The list serializer carries the fields the form needs (Account #,
    // partner_credit_days is on the detail serializer too) — but for safety
    // we pull the detail blob so the mirrors are always populated.
    try {
      const response = await axiosInstance.get(`/api/crm/profiles/${row.id}/`);
      onChange?.(response.data);
    } catch (err) {
      console.warn(
        'CustomerPicker: detail fetch failed, falling back to list row.',
        err
      );
      onChange?.(row);
    }
  };

  const handleClear = () => {
    onChange?.(null);
    setSearchInput('');
  };

  const handleAddUnlinked = () => {
    onChange?.(null);
    onUnlinkedToggle?.(true);
    setIsOpen(false);
  };

  const placeholder = useMemo(() => {
    if (customer) {
      return customer.legal_entity_name || customer.trading_short_name || 'Linked';
    }
    if (showUnlinked) return 'Unlinked customer (text fields below)';
    return placeholderOverride || 'Search Bunker customers...';
  }, [customer, showUnlinked, placeholderOverride]);

  // Escalate stacking context while the dropdown is open so the list
  // overflows over subsequent sections / the sticky stepper / floating
  // buttons. Mirrors the ``zIndex: showDropdown ? 1001 : 1`` pattern used
  // by ``DeliveryPartnerSearch`` (the "Seller contractual" element).
  const dropdownActive = isOpen && !customer && !showUnlinked;

  return (
    <div
      className="fef-customer-picker"
      ref={wrapperRef}
      style={{ zIndex: dropdownActive ? 1001 : 1 }}
    >
      {customer && !showUnlinked && (
        <div className="fef-customer-chip">
          <div className="fef-customer-chip-name">
            {customer.legal_entity_name || customer.trading_short_name || `#${customer.id}`}
            {profileAddress(customer) && (
              <div style={ADDRESS_STYLE}>
                <FontAwesomeIcon icon={faMapMarkerAlt} /> {profileAddress(customer)}
              </div>
            )}
          </div>
          {!disabled && (
            <button
              type="button"
              className="fef-customer-clear-btn"
              onClick={handleClear}
              title="Clear selection"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>
      )}

      {(!customer || showUnlinked) && (
        <div className={`fef-customer-search ${isOpen ? 'fef-open' : ''}`}>
          <FontAwesomeIcon
            icon={faSearch}
            style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#666',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          />
          <input
            type="text"
            className="fef-customer-search-input"
            style={{ paddingLeft: '36px', paddingRight: '36px', width: '100%' }}
            placeholder={placeholder}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onFocus={() => !disabled && setIsOpen(true)}
            disabled={disabled}
          />
          <FontAwesomeIcon
            icon={faChevronDown}
            style={{
              position: 'absolute',
              right: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9ca3af',
              pointerEvents: 'none',
            }}
          />
        </div>
      )}

      {isOpen && !customer && !showUnlinked && (
        <div className="fef-customer-dropdown">
          {loading && (
            <div className="fef-customer-state">
              <FontAwesomeIcon icon={faSpinner} spin /> Searching...
            </div>
          )}
          {!loading && error && (
            <div className="fef-customer-state fef-customer-state-error">
              {error}
            </div>
          )}
          {(() => {
            // Merge live search results with any session-pending stubs
            // matching the search term. Live results come from the
            // backend and may be filtered (e.g. fuels_debtors_only) — we
            // surface pending stubs unconditionally so the user can pick
            // the profile they just created from this same picker.
            const term = (searchInput || '').trim().toLowerCase();
            const liveIds = new Set(results.map(r => r.id));
            const matchingExtras = (extraVisibleProfiles || []).filter(p => {
              if (!p || liveIds.has(p.id)) return false;
              if (!term) return true;
              const hay = (
                (p.legal_entity_name || '') + ' ' + (p.trading_short_name || '')
              ).toLowerCase();
              return hay.includes(term);
            });
            const hasAny = results.length > 0 || matchingExtras.length > 0;

            return (
              <>
                {!loading && !error && !hasAny && (
                  <div className="fef-customer-state">
                    {noResultsCopy}
                  </div>
                )}

                {/* The picker draws the top PAGE_SIZE matches only, and
                    ``/api/crm/quick-search-profiles/`` returns a bare slice
                    with no total, so we cannot say how many were left out --
                    only that some were. Without this line a full list of 20
                    is indistinguishable from "those are all the partners
                    there are", and the trader concludes the customer is not
                    on the system.

                    It fires on ``hasMoreMatches``, not on the length of the
                    list: we ask for PEEK_SIZE and keep PAGE_SIZE, so a
                    twenty-first row coming back is proof there is more, and
                    twenty rows exactly is proof there is not. Saying "the
                    list was cut" over a complete list is what teaches people
                    to ignore the line on the day it is true. */}
                {!loading && !error && hasMoreMatches && (
                  <div className="fef-customer-state">
                    Showing the first {PAGE_SIZE} matches — keep typing to narrow it down.
                  </div>
                )}

                {!loading && !error && results.map((row) => {
                  const showTrading = !!(
                    row.trading_short_name
                    && row.trading_short_name !== row.legal_entity_name
                  );
                  const hasMeta = !!row.monday_account_number
                    || (Array.isArray(row.creditor_debtor_selections)
                      && row.creditor_debtor_selections.length > 0);
                  return (
                    <button
                      key={row.id}
                      type="button"
                      className="fef-customer-option"
                      onClick={() => handlePick(row)}
                    >
                      <div className="fef-customer-option-name">
                        {row.legal_entity_name || row.trading_short_name || `#${row.id}`}
                      </div>
                      {showTrading && (
                        <div className="fef-customer-option-trading">
                          Trading as: {row.trading_short_name}
                        </div>
                      )}
                      {row.head_office_address && (
                        <div style={ADDRESS_STYLE}>
                          <FontAwesomeIcon icon={faMapMarkerAlt} /> {row.head_office_address}
                        </div>
                      )}
                      {hasMeta && (
                        <div className="fef-customer-option-meta">
                          {row.monday_account_number && (
                            <span className="fef-customer-option-meta-item">
                              Acct {row.monday_account_number}
                            </span>
                          )}
                          {Array.isArray(row.creditor_debtor_selections)
                            && row.creditor_debtor_selections.length > 0 && (
                            <span className="fef-customer-option-meta-item">
                              {row.creditor_debtor_selections.join(' / ')}
                            </span>
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}

                {!loading && !error && matchingExtras.map((p) => (
                  <button
                    key={`pending-${p.id}`}
                    type="button"
                    className="fef-customer-option"
                    onClick={() => handlePick(p)}
                  >
                    <div
                      className="fef-customer-option-name"
                      style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}
                    >
                      <span>{p.legal_entity_name || `#${p.id}`}</span>
                      <span
                        title="Created from this draft. Awaiting CRM approval."
                        style={{
                          fontSize: '0.7em',
                          fontWeight: 600,
                          padding: '2px 6px',
                          borderRadius: '4px',
                          backgroundColor: '#fef3c7',
                          color: '#92400e',
                          border: '1px solid #f59e0b',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Pending Approval
                      </span>
                    </div>
                    {p.trading_short_name && (
                      <div className="fef-customer-option-trading">
                        Trading as: {p.trading_short_name}
                      </div>
                    )}
                    {profileAddress(p) && (
                      <div style={ADDRESS_STYLE}>
                        <FontAwesomeIcon icon={faMapMarkerAlt} /> {profileAddress(p)}
                      </div>
                    )}
                  </button>
                ))}

                {onCreatePartner && (
                  <button
                    type="button"
                    className="fef-customer-unlinked-btn"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsOpen(false);
                      onCreatePartner(searchInput || '');
                    }}
                    style={{ color: '#4a6b25' }}
                  >
                    <FontAwesomeIcon icon={faPlus} /> Create CRM partner
                    {searchInput ? ` "${searchInput}"` : ''}
                  </button>
                )}

                {/*
                  Only surface the legacy "+ Add unlinked customer" CTA when
                  the parent has NOT wired the inline CRM creator. With both
                  buttons visible the trader sees two semantically-different
                  "create something" options side-by-side and routinely
                  picks the wrong one (unlinked = free-text, won't reach
                  CRM; create = real Pending CRM stub). Inline-create is the
                  preferred path; unlinked stays available as a fallback for
                  fuel enquiries that explicitly opt into it without
                  enabling inline-create.
                */}
                {allowUnlinked && !onCreatePartner && (
                  <button
                    type="button"
                    className="fef-customer-unlinked-btn"
                    onClick={handleAddUnlinked}
                  >
                    <FontAwesomeIcon icon={faUserPlus} /> Add unlinked customer
                  </button>
                )}
              </>
            );
          })()}
        </div>
      )}

      {showUnlinked && !disabled && (
        <button
          type="button"
          className="fef-customer-back-btn"
          onClick={() => onUnlinkedToggle?.(false)}
        >
          <FontAwesomeIcon icon={faSearch} /> Search existing customer instead
        </button>
      )}
    </div>
  );
};

export default CustomerPicker;
